import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { performHardLogout, useAuthStore, useSettingsStore } from '../store';
import { LogOut, BookOpen, History, Settings, Library, Database, Video, CloudOff, BookMarked, ClipboardCheck } from 'lucide-react';
import { canAccessLearnerArea, canAccessMediaManagement, canAccessQuestionApproval, canAccessQuestionManagement, canAccessStudentManagement, canAccessVocabManagement, getDefaultAuthenticatedRoute, normalizeUserRole } from '../utils/permissions';

interface LayoutDesktopProps {
  pendingCount: number;
}

const LayoutDesktop: React.FC<LayoutDesktopProps> = ({ pendingCount }) => {
  const { user } = useAuthStore();
  const { systemLanguage } = useSettingsStore();
  const location = useLocation();
  const text = systemLanguage === 'en'
    ? {
        dashboard: 'Dashboard',
        history: 'History',
        stats: 'Stats',
        adminPanel: 'Admin Panel',
        questions: 'Questions',
        media: 'Media',
        approvals: 'Approvals',
        vocab: 'Vocab',
        student: 'Student',
        logout: 'Logout',
      }
    : systemLanguage === 'id'
      ? {
          dashboard: 'Beranda',
          history: 'Riwayat',
          stats: 'Statistik',
          adminPanel: 'Panel Admin',
          questions: 'Soal',
          media: 'Media',
          approvals: '审批',
          vocab: 'Kosakata',
          student: 'Siswa',
          logout: 'Keluar',
        }
      : {
          dashboard: '首页',
          history: '历史',
          stats: '统计',
          adminPanel: '管理面板',
          questions: '题目',
          media: '媒体',
          approvals: '审批',
          vocab: '词汇',
          student: '学生',
          logout: '退出登录',
        };

  const homeHref = getDefaultAuthenticatedRoute(user?.role);
  const baseNav = canAccessLearnerArea(user?.role)
    ? [
        { name: text.dashboard, href: '/', icon: BookOpen },
        { name: text.history, href: '/history', icon: History },
        { name: text.stats, href: '/stats', icon: BookMarked },
      ]
    : [];
  const adminNav = [
    { name: text.adminPanel, href: '/admin', icon: Settings },
    ...(canAccessQuestionManagement(user?.role) ? [{ name: text.questions, href: '/admin/questions', icon: Database }] : []),
    ...(canAccessQuestionApproval(user?.role) ? [{ name: text.approvals, href: '/admin/question-approvals', icon: ClipboardCheck }] : []),
    ...(canAccessMediaManagement(user?.role) ? [{ name: text.media, href: '/admin/media', icon: Video }] : []),
    ...(canAccessVocabManagement(user?.role) ? [{ name: text.vocab, href: '/admin/vocab', icon: Library }] : []),
  ];
  const navigation = canAccessStudentManagement(user?.role) ? [...baseNav, ...adminNav] : baseNav;

  return (
    <div className="min-h-screen bg-[#EEF2FF] font-sans">
      <nav className="fixed top-4 left-4 right-4 max-w-7xl mx-auto bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm border border-indigo-100 p-4 flex justify-between items-center z-50">
        <div className="flex items-center gap-2">
          <Link to={homeHref} className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-[#4F46E5] rounded-xl flex items-center justify-center text-white font-bold text-xl group-hover:scale-105 transition-transform">
              HM
            </div>
            <span className="font-bold text-xl text-[#312E81]">Huanle Mandarin</span>
          </Link>

          <div className="flex ml-8 gap-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                    isActive
                      ? 'bg-indigo-50 text-[#4F46E5]'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-indigo-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {pendingCount > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1.5 rounded-xl text-xs font-semibold" title={`${pendingCount} 次提交待上传，将在网络恢复后自动重试`}>
              <CloudOff className="w-3.5 h-3.5" />
              <span>{pendingCount} 待上传</span>
            </div>
          )}
          <div className="flex flex-col items-end">
            <span className="text-sm font-bold text-[#312E81]">{user?.username}</span>
            <span className="text-xs text-gray-500 capitalize">{normalizeUserRole(user?.role)}</span>
          </div>
          <button
            onClick={() => { void performHardLogout({ redirectToLogin: true }); }}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
            title={text.logout}
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </nav>

      <main className="pt-32 pb-12 max-w-7xl mx-auto px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
};

export default LayoutDesktop;
